import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Home, Tag, Package, ShoppingCart, ClipboardList, X, Star } from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;          // desktop expand/collapse
  mobileOpen?: boolean;     // mobile drawer visible?
  onMobileClose?: () => void;
}

/** Menü (değişmedi) */
const navItems = [
  { name: 'Dashboard',        path: '/admin',           icon: LayoutDashboard, exact: true },
  { name: 'Ana Ekran (CMS)',  path: '/anaekran',        icon: Home },
  { name: 'Kategoriler',      path: '/kategoriler',     icon: Tag },
  { name: 'Ürünler',          path: '/urunler',         icon: Package },
  { name: 'Siparişler',       path: '/adminsiparisler', icon: ShoppingCart },
  { name: 'Projeler',         path: '/adminprojeler',   icon: ClipboardList },
  { name: 'Yorumlar',         path: '/yorumlar',         icon: Star },
];

/** /admin bazını ekler */
const withAdminBase = (p: string) => {
  if (p === '/admin' || p.startsWith('/admin/')) return p;
  return `/admin${p.startsWith('/') ? p : `/${p}`}`;
};

const SidebarInner: React.FC<{
  isOpen: boolean;
  onItemClick?: () => void;
}> = ({ isOpen, onItemClick }) => {
  const { pathname } = useLocation();
  return (
    <>
      {/* Logo / Başlık */}
      <div className="flex items-center gap-2 px-4 py-4 border-b">
        <span className="text-lg">🔐</span>
        {isOpen && <span className="font-semibold">Admin Panel</span>}
      </div>

      <nav className="mt-2 p-2 flex flex-col items-stretch space-y-1">
        {navItems.map(({ name, path, icon: Icon, exact }) => {
          const resolvedTo = withAdminBase(path);
          const isDashboard = !!exact;
          return (
            <NavLink
              to={resolvedTo}
              key={path}
              end={isDashboard}
              onClick={onItemClick}
              className={({ isActive }) =>
                [
                  'group relative flex items-center rounded-lg px-3 py-2 text-sm transition-colors',
                  isOpen ? 'justify-start gap-3' : 'justify-center',
                  isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50',
                ].join(' ')
              }
              title={!isOpen ? name : undefined}
            >
              {({ isActive }) => (
                <>
                  <span
                    aria-hidden
                    className={[
                      'absolute left-0 top-1/2 -translate-y-1/2 rounded-r h-6 w-1 transition-opacity',
                      isActive ? 'bg-blue-600 opacity-100' : 'opacity-0',
                    ].join(' ')}
                  />
                  <Icon
                    size={20}
                    strokeWidth={1.75}
                    className={isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'}
                  />
                  {isOpen && (
                    <span className={isActive ? 'font-semibold truncate' : 'truncate'}>
                      {name}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, mobileOpen = false, onMobileClose }) => {
  const expandedWidth = 'w-[240px]';
  const collapsedWidth = 'w-[72px]';

  return (
    <>
      {/* --------- Mobil Drawer (md:hidden) --------- */}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-40 md:hidden bg-white border-r',
          expandedWidth, // mobilde her zaman geniş
          'transform transition-transform duration-200 ease-in-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label="Admin menü"
      >
        {/* Mobil üst bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold">Admin Panel</div>
          <button onClick={onMobileClose} aria-label="Menüyü kapat" className="p-2 rounded hover:bg-gray-100">
            <X size={18} />
          </button>
        </div>
        <SidebarInner isOpen={true} onItemClick={onMobileClose} />
      </aside>

      {/* --------- Desktop Sidebar (md:flex) --------- */}
      <aside
        className={[
          'hidden md:flex md:flex-col bg-white border-r h-screen sticky top-0',
          'transition-[width] duration-200 ease-in-out',
          isOpen ? expandedWidth : collapsedWidth,
        ].join(' ')}
        aria-label="Admin menü"
      >
        <SidebarInner isOpen={isOpen} />
      </aside>
    </>
  );
};

export default Sidebar;
